/**
 * Loads geographic entities (countries and locations) stored in Neo4j through
 * the backend GraphQL proxy. Kept separate from the entity-count query so a
 * failure here never prevents the rest of the dashboard from loading.
 */
export const GEO_ENTITIES_QUERY = `{
    countries { name code }
    locations { name }
}`;

/** Keep only entries with a usable name, normalized to {name, isoCode?}. */
const toNamedEntities = (items) =>
    (Array.isArray(items) ? items : [])
        .filter((item) => item && typeof item.name === 'string' && item.name.trim())
        .map(({ name, code }) => (code ? { name: name.trim(), isoCode: code } : { name: name.trim() }));

/**
 * Fetch countries and locations from Neo4j.
 * @param {Function} fetchImpl - fetch implementation (injectable for tests)
 * @returns {Promise<{countries: Array<{name: string, isoCode?: string}>, locations: Array<{name: string}>}>}
 */
export const fetchGeoEntities = async (fetchImpl = fetch) => {
    const response = await fetchImpl('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: GEO_ENTITIES_QUERY }),
    });
    if (!response.ok) {
        throw new Error(`Geographic entity request failed with status ${response.status}`);
    }
    const { data } = await response.json();
    return {
        countries: toNamedEntities(data?.countries),
        locations: toNamedEntities(data?.locations),
    };
};
